/// <reference types="@types/google.maps" />
/* global google */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { Loader } from '@googlemaps/js-api-loader'
import { useAtomValue } from 'jotai'
import type {
  TrackPoint,
  GoogleType,
  MapRefs,
  MapOverlayRefs,
  VisitRecord,
  SourceFilters,
  ActivityRecord,
} from './types.js'
import { DEFAULT_SOURCE_FILTERS, SOURCE_FALLBACK_LOCATION, SOURCE_LIVE_UPDATES, SOURCE_SLC } from './types.js'
import {
  toSGDate,
  toSGTimeFull,
  toSGDateTimeFull,
  toEpoch,
  formatDuration,
  initSql,
  parseCsvToTrackPoints,
  extractVisitRecords,
  extractActivityRecords,
  dedupeVisits,
  lowerBound,
  upperBound,
} from './helpers.js'
import { renderMapPoints } from './map-renderer.js'
import RangeTimelineSlider from '../../components/RangeTimelineSlider.js'
import { saveDB, listDBs, loadDB, deleteDB } from './db-cache.js'
import type { CachedDbMeta } from './db-cache.js'
import { DbSelector, WelcomeOverlay, MapLegend, MeasureToolbar } from './components.js'
import { useUrlSync, readInitialDbParam, readInitialDateRange } from './use-url-sync.js'
import { useMeasureTool } from './measure-tool.js'
import { themeAtom } from '../../atoms.js'

const SG_OFFSET_MS = 8 * 3_600_000
const MINUTE_MS = 60_000
const HOUR_MS = 3_600_000
const DAY_MS = 24 * HOUR_MS
const MIN_SLIDER_VIEW_SPAN = 6 * HOUR_MS
const MAX_COMFORTABLE_SLIDER_VIEW_SPAN = 2 * DAY_MS
const MAX_SLIDER_TICKS = 240
const SLIDER_TICK_STEPS = [
  30 * MINUTE_MS,
  HOUR_MS,
  3 * HOUR_MS,
  6 * HOUR_MS,
  12 * HOUR_MS,
  DAY_MS,
  2 * DAY_MS,
  7 * DAY_MS,
  30 * DAY_MS,
] as const

const ACTIVITY_CATEGORY_LABELS: Record<number, string> = {
  1: 'Visit',
  2: 'Sleep',
  3: 'Transport',
  4: 'Workout',
}

const ACTIVITY_SOURCE_LABELS: Record<number, string> = {
  1: 'visit',
  2: 'sleep',
  3: 'motion',
  4: 'workout',
  5: 'manual',
  6: 'gpsTrack',
}

const LIGHT_MAP_STYLES: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ saturation: -60 }, { lightness: 30 }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#b0b0b0' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff' }, { weight: 3 }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ lightness: 40 }, { saturation: -80 }] },
  { featureType: 'road', elementType: 'labels', stylers: [{ visibility: 'simplified' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#d4e6f1' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative', elementType: 'labels', stylers: [{ visibility: 'simplified' }] },
]

const DARK_MAP_STYLES: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#1f2937' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#cbd5e1' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#0f172a' }, { weight: 3 }] },
  { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#475569' }] },
  { featureType: 'administrative', elementType: 'labels', stylers: [{ visibility: 'simplified' }] },
  { featureType: 'landscape.natural', elementType: 'geometry', stylers: [{ color: '#1e293b' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#334155' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#111827' }] },
  { featureType: 'road', elementType: 'labels', stylers: [{ visibility: 'simplified' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0f3a4a' }] },
]

/** "YYYY-MM-DDTHH:MM" (SGT local) → epoch ms. */
function sgLocalToEpoch(s: string): number {
  if (!s) return 0
  // Parse as if UTC then subtract the SG offset so the wall-clock matches SGT.
  return Date.parse(s + ':00Z') - SG_OFFSET_MS
}

/** epoch ms → "YYYY-MM-DDTHH:MM" (SGT local, for datetime-local input). */
function epochToSGLocal(ms: number): string {
  return new Date(ms + SG_OFFSET_MS).toISOString().slice(0, 16)
}

/** Generate tick positions for the timeline slider, picking a sensible step
 *  (day / 3h / hour / 30min) based on the domain span. Ticks are aligned to
 *  SGT clock boundaries so labels land on recognizable times. */
function generateSliderTicks(lo: number, hi: number, densityRange = hi - lo): number[] {
  let step: number
  if (densityRange >= 3 * DAY_MS) step = DAY_MS
  else if (densityRange >= 12 * HOUR_MS) step = 3 * HOUR_MS
  else if (densityRange >= 3 * HOUR_MS) step = HOUR_MS
  else step = 30 * MINUTE_MS
  while ((hi - lo) / step > MAX_SLIDER_TICKS) {
    const idx = SLIDER_TICK_STEPS.findIndex((candidate) => candidate >= step)
    const next = SLIDER_TICK_STEPS[Math.min(idx + 1, SLIDER_TICK_STEPS.length - 1)]
    if (!next || next === step) break
    step = next
  }
  const firstK = Math.ceil((lo + SG_OFFSET_MS) / step)
  const lastK = Math.floor((hi + SG_OFFSET_MS) / step)
  const out: number[] = []
  for (let k = firstK; k <= lastK; k++) out.push(k * step - SG_OFFSET_MS)
  return out
}

/** Format a slider tick. Uses "MM-DD" for multi-day spans, else "MM-DD HH:mm". */
function formatSliderTick(ms: number, rangeMs: number): string {
  const iso = new Date(ms + SG_OFFSET_MS).toISOString()
  if (rangeMs >= 3 * DAY_MS) return iso.slice(5, 10)
  return iso.slice(5, 10) + ' ' + iso.slice(11, 16)
}

function formatActivityDateRange(activity: ActivityRecord): string {
  const startDate = toSGDate(activity.startDate)
  const startTime = toSGTimeFull(activity.startDate).slice(0, 5)
  if (!activity.endDate) return `${startDate} ${startTime} - ongoing`

  const endDate = toSGDate(activity.endDate)
  const endTime = toSGTimeFull(activity.endDate).slice(0, 5)
  return startDate === endDate
    ? `${startDate} ${startTime} - ${endTime}`
    : `${startDate} ${startTime} - ${endDate} ${endTime}`
}

function activityDurationText(activity: ActivityRecord): string {
  if (activity.endEpochMs == null) return 'ongoing'
  return formatDuration(Math.max(0, activity.endEpochMs - activity.startEpochMs) / 1000)
}

/** Remove all Google Maps overlays from a ref group and reset it. */
function clearOverlayRefs(refs: MapOverlayRefs): void {
  refs.circles.forEach((c) => c.setMap(null))
  refs.markers.forEach((m) => m.setMap(null))
  refs.circles.length = 0
  refs.markers.length = 0
}

export default function TrackPointViewer() {
  const theme = useAtomValue(themeAtom)
  const isDarkTheme = theme === 'dark'
  const mapRef = useRef<HTMLDivElement>(null)
  const mapInstanceRef = useRef<google.maps.Map | null>(null)
  const googleRef = useRef<GoogleType | null>(null)
  const mapRefsRef = useRef<MapRefs>({ markers: [], polylines: [], circles: [], labels: [], listeners: [] })
  const visitRefsRef = useRef<MapOverlayRefs>({ circles: [], markers: [] })
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null)

  const [allPoints, setAllPoints] = useState<TrackPoint[]>([])
  const [allVisits, setAllVisits] = useState<VisitRecord[]>([])
  const [allActivities, setAllActivities] = useState<ActivityRecord[]>([])
  const [filtered, setFiltered] = useState<TrackPoint[]>([])
  const [dbName, setDbName] = useState('')
  const [mapLoaded, setMapLoaded] = useState(false)
  const [cachedDbs, setCachedDbs] = useState<CachedDbMeta[]>([])
  const [activeDbId, setActiveDbId] = useState<string | null>(null)
  const [showDbDropdown, setShowDbDropdown] = useState(false)

  const [dtFrom, setDtFrom] = useState('')
  const [dtTo, setDtTo] = useState('')
  const [showAccCircles, setShowAccCircles] = useState(false)
  const [showLabels, setShowLabels] = useState(false)
  const [colorBySpeed, setColorBySpeed] = useState(false)
  const [showPointList, setShowPointList] = useState(false)
  const [filters, setFilters] = useState<SourceFilters>(DEFAULT_SOURCE_FILTERS)

  const [stats, setStats] = useState('')
  const [showMobileSidebar, setShowMobileSidebar] = useState(false)
  const [frozenSliderViewSpan, setFrozenSliderViewSpan] = useState<number | undefined | null>(null)

  // mapLoaded gates this so the hook re-runs once googleRef/mapInstanceRef are populated.
  const measure = useMeasureTool(
    mapLoaded ? googleRef.current : null,
    mapLoaded ? mapInstanceRef.current : null,
    isDarkTheme,
  )

  // Data time range (epoch ms) — the outer bounds of the slider. Derived from
  // the first/last loaded trackpoint.
  const dataRange = useMemo<[number, number] | null>(() => {
    if (allPoints.length === 0) return null
    const first = allPoints[0]
    const last = allPoints[allPoints.length - 1]
    if (!first || !last) return null
    return [first.epochMs, last.epochMs]
  }, [allPoints])

  // The slider speaks in epoch ms; our form state is SGT local strings. Bridge
  // in both directions so the two controls stay in sync.
  const windowRange = useMemo<[number, number]>(() => {
    if (!dataRange) return [0, 0]
    const s = dtFrom ? sgLocalToEpoch(dtFrom) : dataRange[0]
    const e = dtTo ? sgLocalToEpoch(dtTo) : dataRange[1]
    return [s, e]
  }, [dtFrom, dtTo, dataRange])

  const handleWindowChange = useCallback((next: [number, number]) => {
    setDtFrom(epochToSGLocal(next[0]))
    setDtTo(epochToSGLocal(next[1]))
  }, [])

  const { setDbInUrl, clearDbFromUrl } = useUrlSync(dtFrom, dtTo)

  // Keep short debug windows wide enough to resize comfortably. The full data
  // range remains scrollable, but the visible viewport zooms around the
  // current selection instead of always squeezing hours into a 2-day strip.
  const computedSliderViewSpan = useMemo(() => {
    if (!dataRange) return undefined
    const dataSpan = dataRange[1] - dataRange[0]
    const selectedSpan = Math.max(MINUTE_MS, Math.min(dataSpan, Math.abs(windowRange[1] - windowRange[0]) || dataSpan))
    const comfortableSpan = Math.max(
      selectedSpan,
      Math.max(MIN_SLIDER_VIEW_SPAN, Math.min(MAX_COMFORTABLE_SLIDER_VIEW_SPAN, selectedSpan * 4)),
    )
    const next = Math.min(dataSpan, comfortableSpan)
    return next < dataSpan ? next : undefined
  }, [dataRange, windowRange])
  const sliderViewSpan = frozenSliderViewSpan === null ? computedSliderViewSpan : frozenSliderViewSpan
  const handleSliderDragStart = useCallback(() => {
    setFrozenSliderViewSpan(computedSliderViewSpan)
  }, [computedSliderViewSpan])
  const handleSliderDragEnd = useCallback(() => {
    setFrozenSliderViewSpan(null)
  }, [])

  const sliderTicks = useMemo(() => {
    if (!dataRange) return undefined
    return generateSliderTicks(dataRange[0], dataRange[1], sliderViewSpan ?? dataRange[1] - dataRange[0])
  }, [dataRange, sliderViewSpan])

  const sliderTickFormatter = useCallback(
    (ms: number) => {
      const range = sliderViewSpan ?? (dataRange ? dataRange[1] - dataRange[0] : 0)
      return formatSliderTick(ms, range)
    },
    [dataRange, sliderViewSpan],
  )

  const filteredActivities = useMemo(() => {
    if (!allActivities.length || !dataRange) return []
    const [rangeStart, rangeEnd] =
      windowRange[0] <= windowRange[1] ? [windowRange[0], windowRange[1]] : [windowRange[1], windowRange[0]]
    return allActivities.filter((activity) => {
      const activityEnd = activity.endEpochMs ?? Number.POSITIVE_INFINITY
      return activity.startEpochMs < rangeEnd && activityEnd >= rangeStart
    })
  }, [allActivities, dataRange, windowRange])

  // Init Google Maps
  useEffect(() => {
    const loader = new Loader({
      apiKey: (import.meta as any).env.VITE_GOOGLE_MAPS_API_KEY || '',
      version: 'weekly',
      libraries: ['geometry'],
    })
    loader.load().then((g) => {
      googleRef.current = g
      // StrictMode double-mount guard: in dev React runs this effect twice;
      // without this check we'd attach two Map instances to the same div,
      // leaving the rendered tiles blank.
      if (mapInstanceRef.current) return
      if (mapRef.current) {
        mapInstanceRef.current = new g.maps.Map(mapRef.current, {
          zoom: 14,
          center: { lat: 1.352, lng: 103.789 },
          mapTypeControl: false,
          styles: isDarkTheme ? DARK_MAP_STYLES : LIGHT_MAP_STYLES,
        })
        infoWindowRef.current = new g.maps.InfoWindow()
        setMapLoaded(true)
      }
    })
  }, [isDarkTheme])

  useEffect(() => {
    mapInstanceRef.current?.setOptions({
      styles: isDarkTheme ? DARK_MAP_STYLES : LIGHT_MAP_STYLES,
    })
  }, [isDarkTheme])

  // Load file (auto-detect format). When `cacheId` is provided the file was
  // loaded from IndexedDB so we skip the save step.
  const loadFile = useCallback(
    async (buffer: ArrayBuffer, fileName: string, cacheId?: string) => {
      let points: TrackPoint[]
      let visits: VisitRecord[] = []
      let activities: ActivityRecord[] = []

      if (fileName.endsWith('.csv')) {
        const text = new TextDecoder().decode(buffer)
        points = parseCsvToTrackPoints(text)
        if (points.length === 0) {
          window.alert('No track point data found in CSV file.')
          return
        }
      } else {
        const SQL = await initSql()
        const db = new SQL.Database(new Uint8Array(buffer))
        const results = db.exec(`
        SELECT id, latitude, longitude, altitude, horizontalAccuracy, speed, course,
               timestamp, source, segmentIndex
        FROM TrackPoint ORDER BY timestamp ASC
      `)

        if (results.length === 0) {
          db.close()
          window.alert('No TrackPoint data found in this database.')
          return
        }

        const cols: string[] = results[0]?.columns ?? []
        const rows: unknown[][] = results[0]?.values ?? []
        points = rows.map((row: unknown[]) => {
          const obj: Record<string, unknown> = {}
          cols.forEach((c: string, idx: number) => (obj[c] = row[idx]))
          return obj as unknown as TrackPoint
        })

        // VisitRecords are optional — older DBs may not have the table.
        visits = dedupeVisits(extractVisitRecords(db))
        activities = extractActivityRecords(db)
        db.close()
      }

      // Pre-compute epoch ms once to avoid repeated Date parsing during
      // filtering and rendering (biggest perf bottleneck).
      for (const p of points) {
        p.epochMs = toEpoch(p.timestamp)
      }
      points.sort((a, b) => a.epochMs - b.epochMs)

      // Save to IndexedDB and update URL (skip if already loaded from cache)
      let dbId = cacheId
      if (!dbId) {
        dbId = await saveDB(fileName, buffer)
        setCachedDbs(await listDBs())
      }
      setActiveDbId(dbId)
      setDbInUrl(dbId, fileName)

      setAllPoints(points)
      setAllVisits(visits)
      setAllActivities(activities)
      setDbName(fileName)

      // Restore date range from URL params, or default to last day
      const saved = readInitialDateRange()
      if (saved) {
        setDtFrom(saved.from)
        setDtTo(saved.to)
      } else {
        const firstPoint = points[0]
        const lastPoint = points[points.length - 1]
        if (firstPoint && lastPoint) {
          const lastDate = toSGDate(lastPoint.timestamp)
          setDtFrom(lastDate + 'T00:00')
          setDtTo(lastDate + 'T23:59')
        }
      }
    },
    [setDbInUrl],
  )

  // Load cached DB list and auto-load from URL on mount
  useEffect(() => {
    listDBs()
      .then(setCachedDbs)
      .catch(() => {})

    const parsed = readInitialDbParam()
    if (!parsed) return

    loadDB(parsed.id).then((entry) => {
      if (entry) {
        loadFile(entry.buffer, entry.fileName, entry.id)
      } else {
        clearDbFromUrl()
      }
    })
  }, [loadFile, clearDbFromUrl])

  // Filter & render TRACKPOINTS when date/time/source/options change.
  // Uses binary search on pre-computed _epoch for O(log n) range lookup
  // instead of O(n) string-based filtering. Map rendering is debounced
  // so rapid slider drags don't re-create thousands of markers per frame.
  useEffect(() => {
    if (!allPoints.length || !mapLoaded) return
    const g = googleRef.current
    const map = mapInstanceRef.current
    const iw = infoWindowRef.current
    if (!g || !map || !iw) return

    // Convert date strings to epoch once, then use numeric comparison
    const fromEpoch = sgLocalToEpoch(dtFrom)
    const toEpochMs = sgLocalToEpoch(dtTo)

    // Binary search: allPoints is sorted by timestamp (ORDER BY timestamp ASC)
    const startIdx = lowerBound(allPoints, fromEpoch)
    const endIdx = upperBound(allPoints, toEpochMs)

    // Source filter only on the narrowed range
    const slice = allPoints.slice(startIdx, endIdx)
    const needSourceFilter = !filters[SOURCE_LIVE_UPDATES] || !filters[SOURCE_FALLBACK_LOCATION] || !filters[SOURCE_SLC]
    const f = needSourceFilter
      ? slice.filter((p) => {
          if (p.source === SOURCE_LIVE_UPDATES && !filters[SOURCE_LIVE_UPDATES]) return false
          if (p.source === SOURCE_FALLBACK_LOCATION && !filters[SOURCE_FALLBACK_LOCATION]) return false
          if (p.source === SOURCE_SLC && !filters[SOURCE_SLC]) return false
          return true
        })
      : slice
    setFiltered(f)

    // Debounce map rendering (80ms) so rapid slider drags coalesce into a
    // single re-render instead of creating/destroying markers per frame.
    const timer = setTimeout(() => {
      const result = renderMapPoints(g, map, iw, mapRefsRef.current, f, {
        colorBySpeed,
        showLabels,
        showAccCircles,
        isDarkMode: isDarkTheme,
      })
      setStats(result.statsText)
    }, 80)
    return () => clearTimeout(timer)
  }, [allPoints, dtFrom, dtTo, colorBySpeed, showLabels, showAccCircles, filters, mapLoaded, isDarkTheme])

  // Render VISIT RECORDS independently of trackpoints, so toggling the visit
  // checkbox doesn't trigger a full trackpoint re-render.
  useEffect(() => {
    if (!mapLoaded) return
    const g = googleRef.current
    const map = mapInstanceRef.current
    const iw = infoWindowRef.current
    if (!g || !map || !iw) return

    clearOverlayRefs(visitRefsRef.current)
    if (!filters.visits || allVisits.length === 0) return

    const fromEpoch = sgLocalToEpoch(dtFrom)
    const toEpochMs = sgLocalToEpoch(dtTo)
    const visitsInRange = allVisits.filter((v) => {
      // Show a visit if it overlaps the date range. Visits with null
      // departureDate are only shown when their arrival is itself in range
      // (otherwise stale records from years ago drag in).
      const arrEpoch = toEpoch(v.arrivalDate)
      if (v.departureDate) {
        const depEpoch = toEpoch(v.departureDate)
        return depEpoch >= fromEpoch && arrEpoch <= toEpochMs
      }
      return arrEpoch >= fromEpoch && arrEpoch <= toEpochMs
    })

    const vr = visitRefsRef.current
    for (const v of visitsInRange) {
      const isOngoing = v.departureDate == null
      const color = isOngoing ? '#FF9500' : '#007AFF'
      const radius = Math.max(15, Math.min(200, v.horizontalAccuracy || 30))
      // Smaller circles get higher zIndex so they're clickable on top of larger ones
      const circleZ = 10000 - Math.round(radius)
      const circle = new g.maps.Circle({
        center: { lat: v.latitude, lng: v.longitude },
        radius,
        fillColor: color,
        fillOpacity: 0.18,
        strokeColor: color,
        strokeOpacity: 0.85,
        strokeWeight: 2,
        map,
        clickable: true,
        zIndex: circleZ,
      })
      const marker = new g.maps.Marker({
        position: { lat: v.latitude, lng: v.longitude },
        map,
        icon: {
          path: g.maps.SymbolPath.CIRCLE,
          scale: 5,
          fillColor: color,
          fillOpacity: 0.95,
          strokeColor: isDarkTheme ? '#0f172a' : '#fff',
          strokeWeight: 1.5,
        },
        zIndex: circleZ + 1,
        title: `Visit #${v.id}`,
      })
      const open = () => {
        const arr = toSGDateTimeFull(v.arrivalDate)
        const dep = v.departureDate ? toSGDateTimeFull(v.departureDate) : '(ongoing)'
        const duration = v.departureDate
          ? formatDuration((toEpoch(v.departureDate) - toEpoch(v.arrivalDate)) / 1000)
          : '—'
        iw.setContent(`
          <div style="font-size:13px; line-height:1.6; min-width:200px;">
            <div style="font-weight:700; margin-bottom:4px;">
              Visit #${v.id} <span style="color:${color};">${isOngoing ? '● ongoing' : '● complete'}</span>
            </div>
            <b>Arrival:</b> ${arr}<br>
            <b>Departure:</b> ${dep}<br>
            <b>Duration:</b> ${duration}<br>
            <b>Accuracy:</b> ${v.horizontalAccuracy.toFixed(0)}m<br>
            <b>Lat/Lng:</b> ${v.latitude.toFixed(6)}, ${v.longitude.toFixed(6)}
          </div>
        `)
        iw.setPosition({ lat: v.latitude, lng: v.longitude })
        iw.open(map)
      }
      circle.addListener('click', open)
      marker.addListener('click', open)
      vr.circles.push(circle)
      vr.markers.push(marker)
    }
  }, [allVisits, dtFrom, dtTo, filters.visits, mapLoaded, isDarkTheme])

  const focusPoint = useCallback(
    (idx: number) => {
      const p = filtered[idx]
      const marker = mapRefsRef.current.markers[idx]
      if (!p || !mapInstanceRef.current || !marker) return
      mapInstanceRef.current.panTo({ lat: p.latitude, lng: p.longitude })
      mapInstanceRef.current.setZoom(18)
      googleRef.current?.maps.event.trigger(marker, 'click')
    },
    [filtered],
  )

  const focusActivity = useCallback((activity: ActivityRecord) => {
    if (
      activity.locationLatitude == null ||
      activity.locationLongitude == null ||
      !mapInstanceRef.current ||
      !Number.isFinite(activity.locationLatitude) ||
      !Number.isFinite(activity.locationLongitude)
    ) {
      return
    }
    mapInstanceRef.current.panTo({ lat: activity.locationLatitude, lng: activity.locationLongitude })
    mapInstanceRef.current.setZoom(17)
  }, [])

  // Drag & drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => e.preventDefault(), [])
  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      const file = e.dataTransfer.files[0]
      if (!file) return
      const reader = new window.FileReader()
      reader.onload = () => loadFile(reader.result as ArrayBuffer, file.name)
      reader.readAsArrayBuffer(file)
    },
    [loadFile],
  )
  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return
      const reader = new window.FileReader()
      reader.onload = () => loadFile(reader.result as ArrayBuffer, file.name)
      reader.readAsArrayBuffer(file)
    },
    [loadFile],
  )

  const switchToDb = useCallback(
    async (meta: CachedDbMeta) => {
      if (meta.id === activeDbId) {
        setShowDbDropdown(false)
        return
      }
      const entry = await loadDB(meta.id)
      if (entry) {
        loadFile(entry.buffer, entry.fileName, entry.id)
      } else {
        // Entry was evicted — refresh list
        setCachedDbs(await listDBs())
      }
      setShowDbDropdown(false)
    },
    [activeDbId, loadFile],
  )

  const handleDeleteDb = useCallback(
    async (e: React.MouseEvent, id: string) => {
      e.stopPropagation()
      await deleteDB(id)
      const updated = await listDBs()
      setCachedDbs(updated)
      // If we deleted the active DB, clear the view
      if (id === activeDbId) {
        setAllPoints([])
        setAllVisits([])
        setAllActivities([])
        setDbName('')
        setActiveDbId(null)
        clearDbFromUrl()
      }
    },
    [activeDbId, clearDbFromUrl],
  )

  const hasData = allPoints.length > 0

  return (
    <div
      className="relative h-[calc(100vh-120px)] overflow-hidden bg-slate-100 text-slate-900 dark:bg-slate-950 dark:text-slate-100 sm:flex"
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* ── Sidebar (left on desktop, bottom sheet on mobile) ── */}
      {hasData && (
        <div
          className={`absolute inset-x-0 bottom-0 z-20 flex max-h-[70vh] flex-col overflow-y-auto bg-white text-xs text-slate-900 shadow-[0_-2px_10px_rgba(0,0,0,0.1)] transition-transform duration-200 dark:bg-slate-900 dark:text-slate-100 dark:shadow-[0_-2px_16px_rgba(0,0,0,0.45)] sm:relative sm:inset-auto sm:z-auto sm:max-h-none sm:w-80 sm:flex-shrink-0 sm:translate-y-0 sm:border-r sm:border-gray-200 sm:shadow-none sm:dark:border-slate-700 ${showMobileSidebar ? 'translate-y-0' : 'translate-y-full sm:translate-y-0'}`}
        >
          {/* Mobile drag handle */}
          <div className="flex justify-center py-1.5 sm:hidden" onClick={() => setShowMobileSidebar(false)}>
            <div className="h-1 w-8 rounded-full bg-gray-300 dark:bg-slate-600" />
          </div>

          <DbSelector
            dbName={dbName}
            activeDbId={activeDbId}
            showDropdown={showDbDropdown}
            setShowDropdown={setShowDbDropdown}
            cachedDbs={cachedDbs}
            onSwitch={switchToDb}
            onDelete={handleDeleteDb}
            onFileChange={handleFileChange}
          />

          {/* Date range */}
          <div className="flex flex-col gap-1 border-b border-gray-100 px-3 py-2 dark:border-slate-700">
            <input
              type="datetime-local"
              value={dtFrom}
              onChange={(e) => setDtFrom(e.target.value)}
              className="w-full rounded border border-gray-300 bg-white px-1.5 py-0.5 text-xs text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:[color-scheme:dark]"
            />
            <input
              type="datetime-local"
              value={dtTo}
              onChange={(e) => setDtTo(e.target.value)}
              className="w-full rounded border border-gray-300 bg-white px-1.5 py-0.5 text-xs text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:[color-scheme:dark]"
            />
          </div>

          {/* Timeline slider */}
          {dataRange && (
            <div className="border-b border-gray-100 px-3 py-2 dark:border-slate-700">
              <RangeTimelineSlider
                min={dataRange[0]}
                max={dataRange[1]}
                step={10 * MINUTE_MS}
                value={windowRange}
                minSpan={5 * MINUTE_MS}
                maxSpan={dataRange[1] - dataRange[0]}
                onChange={handleWindowChange}
                onDragStart={handleSliderDragStart}
                onDragEnd={handleSliderDragEnd}
                formatTick={sliderTickFormatter}
                ticks={sliderTicks}
                viewSpan={sliderViewSpan}
              />
            </div>
          )}

          {/* Filters & options */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-gray-100 px-3 py-2 text-slate-700 dark:border-slate-700 dark:text-slate-200">
            <label className="flex items-center gap-1">
              <input
                type="checkbox"
                checked={filters[SOURCE_LIVE_UPDATES]}
                onChange={(e) => setFilters((f) => ({ ...f, [SOURCE_LIVE_UPDATES]: e.target.checked }))}
              />
              <span className="text-[#34C759]">●</span>liveUpdates
            </label>
            <label className="flex items-center gap-1">
              <input
                type="checkbox"
                checked={filters[SOURCE_FALLBACK_LOCATION]}
                onChange={(e) => setFilters((f) => ({ ...f, [SOURCE_FALLBACK_LOCATION]: e.target.checked }))}
              />
              <span className="text-[#FF9500]">●</span>startUpdating
            </label>
            <label className="flex items-center gap-1">
              <input
                type="checkbox"
                checked={filters[SOURCE_SLC]}
                onChange={(e) => setFilters((f) => ({ ...f, [SOURCE_SLC]: e.target.checked }))}
              />
              <span className="text-[#AF52DE]">●</span>slc
            </label>
            <label className="flex items-center gap-1">
              <input
                type="checkbox"
                checked={filters.visits}
                onChange={(e) => setFilters((f) => ({ ...f, visits: e.target.checked }))}
              />
              <span className="text-[#007AFF]">●</span>CLVisit
            </label>
            <label className="flex items-center gap-1">
              <input type="checkbox" checked={showAccCircles} onChange={(e) => setShowAccCircles(e.target.checked)} />
              Accuracy
            </label>
            <label className="flex items-center gap-1">
              <input type="checkbox" checked={showLabels} onChange={(e) => setShowLabels(e.target.checked)} /> #
            </label>
            <label className="flex items-center gap-1">
              <input type="checkbox" checked={colorBySpeed} onChange={(e) => setColorBySpeed(e.target.checked)} />
              Speed
            </label>
            <button
              onClick={() => setShowPointList((v) => !v)}
              className="rounded bg-gray-200 px-2 py-0.5 text-gray-700 hover:bg-gray-300 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600"
            >
              {showPointList ? 'Hide List' : 'List'}
            </button>
          </div>

          {/* Stats */}
          {stats && (
            <div className="border-b border-gray-100 px-3 py-2 text-gray-600 dark:border-slate-700 dark:text-slate-300">
              {stats}
            </div>
          )}

          {/* Point list (inline in sidebar) */}
          {showPointList && (
            <div className="max-h-60 overflow-y-auto border-b border-gray-100 dark:border-slate-700">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="sticky top-0 bg-gray-100 text-left dark:bg-slate-800 dark:text-slate-200">
                    <th className="px-2 py-1">#</th>
                    <th className="px-2 py-1">Time</th>
                    <th className="px-2 py-1">Acc</th>
                    <th className="px-2 py-1">Spd</th>
                    <th className="px-2 py-1">Gap</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p, i) => {
                    const gapSec = i > 0 ? (toEpoch(p.timestamp) - toEpoch(filtered[i - 1]!.timestamp)) / 1000 : 0
                    const isGap = gapSec >= 120
                    const isLowAcc = p.horizontalAccuracy != null && p.horizontalAccuracy >= 65
                    return (
                      <tr
                        key={p.id}
                        className={`cursor-pointer border-t border-gray-100 hover:bg-blue-50 dark:border-slate-700 dark:hover:bg-blue-500/10 ${
                          isGap
                            ? 'bg-yellow-50 text-yellow-800 dark:bg-yellow-500/15 dark:text-yellow-200'
                            : isLowAcc
                              ? 'text-red-600 dark:text-red-300'
                              : 'dark:text-slate-200'
                        }`}
                        onClick={() => focusPoint(i)}
                      >
                        <td className="px-2 py-1">{i + 1}</td>
                        <td className="px-2 py-1">{toSGTimeFull(p.timestamp)}</td>
                        <td className="px-2 py-1">
                          {p.horizontalAccuracy != null ? p.horizontalAccuracy.toFixed(0) : '—'}
                        </td>
                        <td className="px-2 py-1">{p.speed != null ? (p.speed * 3.6).toFixed(1) : '—'}</td>
                        <td className="px-2 py-1">{i > 0 ? formatDuration(gapSec) : '—'}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Activity list */}
          <div className="min-h-0 flex-1 border-b border-gray-100 dark:border-slate-700">
            <div className="sticky top-0 z-10 flex items-center justify-between bg-white px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:bg-slate-900 dark:text-slate-400">
              <span>Activities</span>
              <span>{filteredActivities.length}</span>
            </div>
            {filteredActivities.length === 0 ? (
              <div className="px-3 py-4 text-slate-400 dark:text-slate-500">No overlapping activities</div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-slate-700">
                {filteredActivities.map((activity) => {
                  const typeColor = activity.typeColor || '#64748b'
                  const categoryLabel = ACTIVITY_CATEGORY_LABELS[activity.category] ?? `Category ${activity.category}`
                  const sourceLabel = ACTIVITY_SOURCE_LABELS[activity.source] ?? `source ${activity.source}`
                  const locationText =
                    activity.locationName || (activity.locationId ? `Location #${activity.locationId}` : null)
                  const canFocus =
                    activity.locationLatitude != null &&
                    activity.locationLongitude != null &&
                    Number.isFinite(activity.locationLatitude) &&
                    Number.isFinite(activity.locationLongitude)
                  return (
                    <button
                      key={activity.id}
                      type="button"
                      className={`block w-full border-l-4 bg-white px-3 py-2 text-left dark:bg-slate-900 ${
                        canFocus
                          ? 'hover:bg-blue-50 dark:hover:bg-blue-500/10'
                          : 'cursor-default hover:bg-white dark:hover:bg-slate-900'
                      }`}
                      style={{ borderLeftColor: typeColor }}
                      onClick={() => {
                        if (canFocus) focusActivity(activity)
                      }}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex min-w-0 items-center gap-1.5">
                            <span className="truncate font-semibold text-slate-800 dark:text-slate-100">
                              {activity.typeName || categoryLabel}
                            </span>
                            <span className="shrink-0 rounded bg-slate-100 px-1 py-0.5 text-[10px] text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                              #{activity.id}
                            </span>
                          </div>
                          <div className="mt-0.5 text-slate-500 dark:text-slate-400">
                            {formatActivityDateRange(activity)}
                          </div>
                        </div>
                        <div className="shrink-0 text-right text-slate-600 dark:text-slate-300">
                          {activityDurationText(activity)}
                        </div>
                      </div>
                      <div className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-[11px] text-slate-500 dark:text-slate-400">
                        <span>{categoryLabel}</span>
                        <span>{sourceLabel}</span>
                        {locationText && <span className="truncate">{locationText}</span>}
                        {activity.parentActivityId != null && <span>parent #{activity.parentActivityId}</span>}
                      </div>
                      {activity.notes && (
                        <div className="mt-1 line-clamp-2 text-[11px] text-slate-600 dark:text-slate-300">
                          {activity.notes}
                        </div>
                      )}
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Map (full-screen on mobile, flex-1 on desktop) ── */}
      <div className="absolute inset-0 bg-slate-200 dark:bg-slate-950 sm:relative sm:flex-1">
        <div ref={mapRef} className="h-full w-full" />

        {!hasData && (
          <WelcomeOverlay
            cachedDbs={cachedDbs}
            onSwitch={switchToDb}
            onDelete={handleDeleteDb}
            onFileChange={handleFileChange}
          />
        )}

        {hasData && <MapLegend />}

        {hasData && (
          <MeasureToolbar
            active={measure.active}
            totalMeters={measure.totalMeters}
            pointCount={measure.pointCount}
            onToggle={measure.toggle}
            onClear={measure.clear}
          />
        )}

        {/* Mobile toggle button */}
        {hasData && !showMobileSidebar && (
          <button
            className="absolute bottom-4 left-1/2 z-10 -translate-x-1/2 rounded-full bg-white px-4 py-2 text-xs font-medium text-gray-700 shadow-lg active:bg-gray-100 dark:bg-slate-800 dark:text-slate-100 dark:shadow-black/40 dark:active:bg-slate-700 sm:hidden"
            onClick={() => setShowMobileSidebar(true)}
          >
            Controls ▲
          </button>
        )}
      </div>

      {/* Mobile backdrop */}
      {hasData && showMobileSidebar && (
        <div
          className="absolute inset-0 z-10 bg-black/20 dark:bg-black/40 sm:hidden"
          onClick={() => setShowMobileSidebar(false)}
        />
      )}
    </div>
  )
}
